import React, { useState } from 'react';
import { ReportType } from '../types/ReportTypes';
import { SensorData, DoorSensorData } from '../../../types/SensorData';
import { Button, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Autocomplete, Checkbox, ListItemText, OutlinedInput, Box } from '@mui/material';
// @ts-ignore - PageHeader is a JSX component
import { PageHeader } from '../../../../shared/PageHeader/PageHeader';
import {OlangItem} from '../../../../shared/Olang/user-interface/OlangItem/OlangItem';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { fr } from 'date-fns/locale';

interface NewRapportFormProps {
  reportType: ReportType;
  sensors?: (SensorData | DoorSensorData)[];
  onBack: () => void;
  onSubmit: (formData: any) => void;
}

const NewRapportForm: React.FC<NewRapportFormProps> = ({ reportType, sensors = [], onBack, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: new Date(),
    endDate: new Date(),
    selectedItems: [] as string[],
    format: 'pdf',
    includeCharts: true,
    includeDetails: true
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Generate available items based on report type and sensors
  const availableItems = React.useMemo(() => {
    if (reportType.type === 'sensor') {
      return sensors.map(sensor => `${sensor.label} (${sensor.macAddr})`);
    }
    // For other report types, you can add specific logic
    return sensors.map(sensor => `${sensor.label} (${sensor.macAddr})`);
  }, [sensors, reportType.type]);

  // Filter items based on search term
  const filteredItems = React.useMemo(() => {
    if (!searchTerm) return availableItems;
    return availableItems.filter(item => 
      item.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [availableItems, searchTerm]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      type: reportType.type,
      createdAt: new Date().toISOString()
    });
  };

  const handleItemToggle = (item: string) => {
    setFormData(prev => ({
      ...prev,
      selectedItems: prev.selectedItems.includes(item)
        ? prev.selectedItems.filter(i => i !== item)
        : [...prev.selectedItems, item]
    }));
  };

  return (
    <div className="bg-gray-100 h-full relative border-round-md" 
         >
      
      {/* Page Header */}
      {/* @ts-ignore */}
      <PageHeader
        title="report.new"
        onBack={onBack}
        info={
          <div className="flex align-items-center gap-3">
            <div className="flex align-items-center gap-2">
              <i className={`${reportType.icon} text-xl text-primary`}></i>
              <span className="text-lg font-semibold">{reportType.title}</span>
            </div>
            {formData.selectedItems.length > 0 && (
              <>
                <div className="border-left-2 border-300" style={{ height: '30px' }}></div>
                <div className="flex align-items-center gap-2">
                  <i className="pi pi-check-square text-xl text-primary"></i>
                  <span className="text-lg font-semibold">
                    {formData.selectedItems.length} élément{formData.selectedItems.length > 1 ? 's' : ''} sélectionné{formData.selectedItems.length > 1 ? 's' : ''}
                  </span>
                </div>
              </>
            )}
          </div>
        }
        className="m-3"
      />

      {/* Form Content */}
      <div className="flex-1 overflow-auto p-4">
        <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Basic Information */}
            <div className="bg-white p-4 rounded-md shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <i className={`fas ${reportType.icon} text-blue-600 mr-2`}></i>
                Informations générales
              </h3>
              
              <div className="space-y-4">
                <TextField
                  fullWidth
                  label="Titre du rapport"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
                
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
            </div>

            {/* Date Range */}
            <div className="bg-white p-4 rounded-md shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <i className="fas fa-calendar text-blue-600 mr-2"></i>
                Période
              </h3>
              
              <div className="flex gap-4 w-full">
                <div className="flex-1">
                  <TextField
                    type="datetime-local"
                    label="Date et heure de début"
                    value={formData.startDate.toISOString().slice(0, 16)}
                    onChange={(e) => setFormData(prev => ({ ...prev, startDate: new Date(e.target.value) }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </div>
                
                <div className="flex-1">
                  <TextField
                    type="datetime-local"
                    label="Date et heure de fin"
                    value={formData.endDate.toISOString().slice(0, 16)}
                    onChange={(e) => setFormData(prev => ({ ...prev, endDate: new Date(e.target.value) }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </div>
              </div>
            </div>

            {/* Item Selection */}
            <div className="bg-white p-4 rounded-md shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <i className="fas fa-list text-blue-600 mr-2"></i>
                Sélection des éléments
              </h3>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-3">
                  Sélectionnez les capteurs à inclure dans le rapport :
                </p>
                
                {/* Searchable Dropdown with Checkboxes */}
                <FormControl fullWidth>
                  <InputLabel>Capteurs disponibles</InputLabel>
                  <Select
                    multiple
                    value={formData.selectedItems}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      setFormData(prev => ({ ...prev, selectedItems: value }));
                    }}
                    input={<OutlinedInput label="Capteurs disponibles" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                          width: 'auto',
                        },
                      },
                    }}
                  >
                    {/* Search Input */}
                    <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
                      <TextField
                        size="small"
                        placeholder="Rechercher un capteur..."
                        value={searchTerm}
                        onChange={(e) => {
                          e.stopPropagation();
                          setSearchTerm(e.target.value);
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        fullWidth
                        variant="outlined"
                      />
                    </Box>
                    
                    {/* Filtered Items with Checkboxes */}
                    {filteredItems.map((item) => (
                      <MenuItem key={item} value={item}>
                        <Checkbox 
                          checked={formData.selectedItems.includes(item)}
                          size="small"
                        />
                        <ListItemText primary={item} />
                      </MenuItem>
                    ))}
                    
                    {filteredItems.length === 0 && searchTerm && (
                      <MenuItem disabled>
                        <ListItemText primary="Aucun capteur trouvé" />
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
                
                {formData.selectedItems.length > 0 && (
                  <p className="text-sm text-green-600 mt-2">
                    {formData.selectedItems.length} capteur(s) sélectionné(s)
                  </p>
                )}
              </div>
            </div>

            {/* Options */}
            <div className="bg-white p-4 rounded-md shadow-sm">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <i className="fas fa-cog text-blue-600 mr-2"></i>
                Options du rapport
              </h3>
              
              <div className="space-y-4">
                <FormControl fullWidth>
                  <InputLabel>Format de sortie</InputLabel>
                  <Select
                    value={formData.format}
                    onChange={(e) => setFormData(prev => ({ ...prev, format: e.target.value }))}
                  >
                    <MenuItem value="pdf">PDF</MenuItem>
                    <MenuItem value="excel">Excel</MenuItem>
                    <MenuItem value="csv">CSV</MenuItem>
                  </Select>
                </FormControl>
                
                <div className="flex flex-col space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.includeCharts}
                      onChange={(e) => setFormData(prev => ({ ...prev, includeCharts: e.target.checked }))}
                      className="mr-2"
                    />
                    Inclure les graphiques
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.includeDetails}
                      onChange={(e) => setFormData(prev => ({ ...prev, includeDetails: e.target.checked }))}
                      className="mr-2"
                    />
                    Inclure les détails techniques
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4 pt-4">
              <Button
                variant="outlined"
                onClick={onBack}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!formData.title || formData.selectedItems.length === 0}
              >
                Générer le rapport
              </Button>
            </div>
          </form>
      </div>
    </div>
  );
};

export default NewRapportForm;
